import * as Sentry from "@sentry/react";
import { createContext, useCallback, useContext, useRef } from "react";

import {
  GtmEvent,
  GtmEventAppOpen,
  GtmEventOnce,
  GtmEventTabClick,
  GtmEventTabView,
  GtmEventUserId,
} from "./events";
import { useCurrentNode } from "../current-note-provider";
import { isLocalhost } from "~/util/env-utils";
import { useTenantId } from "~/util/use-active-tenant-id";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

type GtmEventsContextProps = {
  sendEvent: (event: GtmEvent) => void;
  sendOnce: (event: GtmEventOnce) => void;
};

const GtmEventsContext = createContext<GtmEventsContextProps | undefined>(
  undefined,
);

export const GtmEventsProvider: React.FC<
  React.PropsWithChildren<{ enabled?: boolean }>
> = ({ children, enabled = false }) => {
  const sentEventsRef = useRef<Set<GtmEvent["eventName"]>>(new Set());

  const sendEvent = useCallback(
    (event: GtmEvent) => {
      if (!enabled) {
        return;
      } else if (isLocalhost) {
        console.info("would have dispatched", event);
      }

      if (Array.isArray(window.dataLayer)) {
        try {
          window.dataLayer.push({
            event: event.eventName,
            ...event.parameters,
            env: process.env.DEPLOYMENT_ENV === "prod" ? "app" : "dev",
          });
        } catch (error) {
          console.error(`Failed to send event ${event.eventName}:`, error);
        }
      } else {
        console.error("dataLayer is not available. Event not sent.");
      }
    },
    [enabled],
  );

  const sendOnce = useCallback(
    (event: GtmEventOnce) => {
      if (!sentEventsRef.current.has(event.eventName)) {
        sendEvent(event);
        sentEventsRef.current.add(event.eventName);
      }
    },
    [sendEvent],
  );

  return (
    <GtmEventsContext.Provider
      value={{
        sendEvent,
        sendOnce,
      }}
    >
      {children}
    </GtmEventsContext.Provider>
  );
};

export const useGtmEvents = (): GtmEventsContextProps => {
  const context = useContext(GtmEventsContext);
  if (!context) {
    throw new Error("useGtmEvents must be used within a GtmEventsProvider");
  }
  return context;
};

export const useSendGtmEvents = (): {
  sendTabEvent({
    eventName,
    target,
    bottomNavigation,
  }: {
    eventName: GtmEventTabClick["eventName"] | GtmEventTabView["eventName"];
    target:
      | GtmEventTabClick["parameters"]["target"]
      | GtmEventTabView["parameters"]["target"];
    bottomNavigation: boolean;
  }): void;
  sendAudioRecordStart(): void;
  sendAudioMessageSent({ duration }: { duration: number }): void;
  sendTextMessageSent({ hasFileAttached }: { hasFileAttached: boolean }): void;
  sendAttachmentClicked(): void;
  sendUserHasStartedSignup(onMount?: boolean): void;
  sendUserHasCreatedAccount(onMount?: boolean): void;
  sendUserHasConfirmedAccount(onMount?: boolean): void;
  sendUserHasFinishedSignup(onMount?: boolean): void;
  sendUserHasInteractedWithSignupForm(onMount?: boolean): void;
  sendIdentifyUserBasedOnId(parameters: GtmEventUserId["parameters"]): void;
  sendAppOpen(parameters: GtmEventAppOpen["parameters"]): void;
} => {
  const context = useContext(GtmEventsContext);
  if (!context) {
    throw new Error("useGtmEvents must be used within a GtmEventsProvider");
  }

  const { sendEvent, sendOnce } = context;
  const { node } = useCurrentNode();
  const { disease } = useTenantId();

  const getSafeNode = (
    errorMessage: string,
    extra?: Record<string, unknown>,
  ): typeof node | null => {
    if (node) {
      return node;
    }

    Sentry.captureException(new Error(errorMessage), {
      level: "warning",
      extra,
    });
    return null;
  };

  return {
    sendTabEvent({
      eventName,
      target,
      bottomNavigation,
    }: {
      eventName: GtmEventTabClick["eventName"] | GtmEventTabView["eventName"];
      target:
        | GtmEventTabClick["parameters"]["target"]
        | GtmEventTabView["parameters"]["target"];
      bottomNavigation: boolean;
    }) {
      const safeNode = getSafeNode(
        `node not existing when dispatching gtm for ${eventName}/${target}`,
        {
          target,
          bottomNavigation,
        },
      );
      if (!safeNode) return;

      sendEvent({
        eventName,
        parameters: {
          target,
          disease,
          bottomNavigation,
          node_type: safeNode.type,
          node_subtype: safeNode.subtype,
          node_id: safeNode.node_id,
        },
      });
    },
    sendAudioRecordStart() {
      const safeNode = getSafeNode(
        "node not existing when dispatching gtm for audio_record_start",
      );
      if (!safeNode) return;

      sendEvent({
        eventName: "audio_record_start",
        parameters: {
          disease,
          node_type: safeNode.type,
          node_subtype: safeNode.subtype,
          node_id: safeNode.node_id,
        },
      });
    },
    sendAudioMessageSent({ duration }: { duration: number }) {
      const safeNode = getSafeNode(
        "node not existing when dispatching gtm for audio_message_sent",
      );
      if (!safeNode) return;

      sendEvent({
        eventName: "audio_message_sent",
        parameters: {
          disease,
          voice_duration: duration,
          node_type: safeNode.type,
          node_subtype: safeNode.subtype,
          node_id: safeNode.node_id,
        },
      });
    },
    sendTextMessageSent({ hasFileAttached }: { hasFileAttached: boolean }) {
      const safeNode = getSafeNode(
        "node not existing when dispatching gtm for text_message_sent",
      );
      if (!safeNode) return;

      sendEvent({
        eventName: "text_message_sent",
        parameters: {
          disease,
          node_type: safeNode.type,
          node_subtype: safeNode.subtype,
          node_id: safeNode.node_id,
          file_attached: hasFileAttached,
        },
      });
    },
    sendAttachmentClicked() {
      const safeNode = getSafeNode(
        "node not existing when dispatching gtm for attachment_clicked",
      );
      if (!safeNode) return;

      sendEvent({
        eventName: "attachment_clicked",
        parameters: {
          disease,
          node_type: safeNode.type,
          node_subtype: safeNode.subtype,
          node_id: safeNode.node_id,
        },
      });
    },
    sendUserHasStartedSignup() {
      sendOnce({ eventName: "signup_started" });
    },
    sendUserHasCreatedAccount() {
      sendOnce({ eventName: "account_created" });
    },
    sendUserHasConfirmedAccount() {
      sendOnce({ eventName: "account_confirmed" });
    },
    sendUserHasFinishedSignup() {
      sendOnce({ eventName: "signup_finished" });
    },
    sendUserHasInteractedWithSignupForm() {
      sendOnce({ eventName: "signup_form_touched" });
    },
    sendIdentifyUserBasedOnId(parameters) {
      sendOnce({
        eventName: "user_id",
        parameters,
      });
    },
    sendAppOpen(parameters) {
      sendOnce({ eventName: "app_open", parameters });
    },
  };
};
